import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { useLocation } from "react-router-dom";
import { SEO_LOCALE_URL_STRATEGY } from "@/lib/seo";
import { detectLocaleFromPathname } from "@/lib/localeRouting";

type Lang = "tr" | "en";

export type { Lang };

interface LanguageContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: string) => string;
}

const translations: Record<Lang, Record<string, string>> = {
  tr: {
    // Common
    "common.loading": "Yükleniyor...",
    "common.error": "Bir hata oluştu.",
    "common.more": "Daha Fazla",

    // Nav
    "nav.services": "Hizmetler",
    "nav.solutions": "Çözümler",
    "nav.projects": "Projeler",
    "nav.tools": "Araçlar",
    "nav.about": "Hakkımızda",
    "nav.contact": "İletişim",
    "nav.blog": "Blog",
    "nav.cta": "Proje Başlat",

    // Hero
    "hero.tag": "PRODUCT STUDIO",
    "hero.brand": "Figtures",
    "hero.title1": "Neye ihtiyacınız",
    "hero.title2": "olduğunu anlatın.",
    "hero.title3": "Gerisini biz halledelim.",
    "hero.cta1": "Projenizi Anlatın",
    "hero.cta2": "Çözümlerimiz",

    "home.geoDirectAnswer":
      "Global remote product studio · AI, özel yazılım, SaaS/MVP, mobil · Türkiye, Avrupa, MENA, UK, US ve dünya geneli · figtures.com/contact",
    "home.brandAnswer":
      "Figtures, İstanbul merkezli ve global remote çalışan bir product studio'dur. Özel yazılım, AI entegrasyonu, SaaS ve mobil uygulama geliştirme hizmetleri sunar.",

    // Marquee
    "marquee.text":
      "AI IMPLEMENTATION • SAAS DEVELOPMENT • PRODUCT DESIGN • FULL-STACK ENGINEERING • DIGITAL TRANSFORMATION • ",

    // Services
    "services.tag": "UZMANLIK ALANLARIMIZ",
    "services.seoTitle": "Özel Yazılım ve AI Geliştirme Hizmetleri",
    "services.title": "Siz anlatın, biz çözelim.",
    "services.desc":
      "AI ürünleri, SaaS MVP ve özel yazılım. Keşiften canlıya tek ekip.",

    "solutions.tag": "ÇÖZÜMLER",
    "solutions.seoTitle": "Product Studio Çözümleri ve Teslimat Modeli",
    "solutions.title": "Product studio olarak nasıl çalışıyoruz?",
    "solutions.desc":
      "Figtures; AI geliştirmeden SaaS/MVP, custom software, mobil uygulama ve UI/UX tasarıma kadar discovery’dan production’a tek ekip modeliyle çalışan global bir product studio’dur.",
    "solutions.studioTitle": "Figtures kimdir?",
    "solutions.studioBody":
      "Startup ve kurumsal ekiplerle ürün stratejisi, tasarım ve mühendisliği aynı hatta yürütüyoruz. Aşağıdaki çözüm sayfaları belirli iş problemlerine odaklanır. Tüm hizmet kapsamımız için Hizmetler bölümüne bakın.",
    "solutions.featuredTitle": "Öne çıkan çözüm alanları",
    "solutions.allTitle": "Tüm çözüm sayfaları",
    "solutions.servicesCta": "Tüm hizmet kataloğu",
    "solutions.projectsCta": "Projelerimiz",
    "solutions.contactCta": "Proje brief’i gönder",
    "solutions.servicesNote":
      "Custom software, web geliştirme, SaaS/MVP, mobil uygulama ve startup product engineering hizmetleri Hizmetler sayfasında detaylandırılır.",

    "programmatic.hub.title": "Şehir bazlı çözümler",
    "programmatic.hub.subtitle":
      "Hangi lokasyonda hangi uzmanlığı aradığınızı seçin; özet sayfalar üzerinden içeriğe bağlanır.",
    "programmatic.sameCity": "Bu şehirde diğer hizmetler",
    "programmatic.sameService": "Bu hizmet diğer şehirlerde",
    "programmatic.cta": "Teklif iste",

    // Projects
    "projects.tag": "PROJELERİMİZ",
    "projects.seoTitle": "Yazılım Projeleri ve Case Study'ler",
    "projects.title": "Geliştirdiğimiz çözümler.",
    "projects.desc":
      "Her proje benzersiz bir meydan okumadır. İşte bazı çözümlerimiz.",
    "projects.view": "Detayları Gör",
    "projects.all": "Tüm Projeleri Gör",
    "projects.notFound": "Proje bulunamadı",
    "projects.back": "Projelere Dön",
    "projects.visit": "Projeyi canlı inceleyin",
    "projects.visitSub":
      "Demo, case study veya kaynak kod linkleri üzerinden devam edin.",
    "projects.openLink": "Linki Aç",
    "seo.page.projects.title": "Yazılım Projeleri ve Case Study'ler",
    "seo.page.projects.description":
      "AI, SaaS, kurumsal web ve ürün arayüzü projelerimiz: kullanılan teknolojiler, çözülen problemler ve teslim edilen sonuçlar. Türkiye ve global müşteriler.",
    "projects.p1.title": "3D Yanında: Dijital Üretim",
    "projects.p1.desc":
      "Endüstriyel parça yeniden üretimi için 3D tarama, CAD ve hassas baskı sürecini anlatan kurumsal vitrin.",
    "projects.p1.tags": "Dijital Üretim,Endüstriyel 3D,Türkiye",
    "projects.p2.title": "ailem · Aselsan",
    "projects.p2.desc":
      "Kurumsal portal için sade giriş akışı ve “Bi’ Tıkla Yanında” mesajıyla uyumlu kimlik.",
    "projects.p2.tags": "Kurumsal Portal,Giriş UX,Kimlik",
    "projects.p3.title": "Outfica: AI Moda",
    "projects.p3.desc":
      "Yapay zeka destekli moda araçları ve katalog deneyimi için karanlık tema SaaS paneli.",
    "projects.p3.tags": "AI,Fashion,SaaS",
    "projects.p4.title": "Ztech EdTech",
    "projects.p4.desc":
      "Çocuklara teknoloji ve yapay zeka eğitimi sunan markanın ana sayfa ve kampanya düzeni.",
    "projects.p4.tags": "EdTech,Yapay Zeka,Eğitim",
    "projects.p5.title": "Royal Seginus Hotel",
    "projects.p5.desc":
      "Stone Group çatısı altında rezervasyon çubuğu ve güçlü görsel anlatımla otel ana sayfası.",
    "projects.p5.tags": "Otelcilik,Rezervasyon,Turizm",
    "projects.p6.title": "Kolektif Fuarcılık",
    "projects.p6.desc":
      "International exhibition partner kimliği ile fuarcılık portföyü ve kurumsal anlatım.",
    "projects.p6.tags": "Fuarcılık,Portfolio,Kurumsal",
    "projects.p7.title": "LC Waikiki: Kampanya",
    "projects.p7.desc":
      "Yüksek trafikli moda perakendesi için sezon kampanyası ve kategori mimarisi.",
    "projects.p7.tags": "E-ticaret,Perakende,Moda",
    "projects.p8.title": "evarkadaşı",
    "projects.p8.desc":
      "Oda ve ev arkadaşı arayan kullanıcılar için arama odaklı hero ve şehir keşfi.",
    "projects.p8.tags": "İlan Platformu,Kiralık,Şehir",
    "projects.p9.title": "Bool · Knorr Professional",
    "projects.p9.desc":
      "Gıda tedarik ve markalı Knorr kampanyası için B2B kurumsal vitrin.",
    "projects.p9.tags": "Gıda Tedarik,B2B,Marka",
    "projects.p10.title": "Backdoor Tickets",
    "projects.p10.desc":
      "Etkinlik girişi ve promoter operasyonları için sahne odaklı ticketing arayüzü.",
    "projects.p10.tags": "Bilet,Etkinlik,SaaS",
    "projectDetail.back": "Projelere Dön",
    "projectDetail.challengeTitle": "Tespit ettiğimiz sorun",
    "projectDetail.solutionTitle": "Çözüm yaklaşımı",
    "projectDetail.implementationTitle": "Ne uyguladık",
    "projectDetail.resultsTag": "SONUÇLAR",
    "projectDetail.resultsTitle": "Elde edilen sonuçlar.",
    "projectDetail.galleryTag": "GALERİ",
    "projectDetail.galleryTitle": "Ekran görüntüleri.",
    "projectDetail.ctaTitle": "Benzer bir projeniz mi var?",
    "projectDetail.ctaDesc":
      "Projenizi birlikte hayata geçirelim. Hemen iletişime geçin.",
    "projectDetail.ctaButton": "Proje Başlat",
    "projectDetail.p1.longDesc":
      "Dijital üretim atölyesi için tarama hassasiyeti, hızlı prototipleme ve tamamlanan proje sayısı gibi güven metriklerini öne çıkaran; oto yedek parça ve endüstriyel kullanım senaryolarını anlatan kurumsal üst vitrin.",
    "projectDetail.p1.challenge":
      "Atölyenin dijital vitrini teknik yetkinliği ve hızını anlatmıyor; potansiyel B2B müşteriler güven sinyali bulmakta zorlanıyordu.",
    "projectDetail.p1.solution":
      "SCAN→CAD→PRINT akışını merkeze alan, metrik odaklı bir kurumsal site ve lead toplama deneyimi tasarladık.",
    "projectDetail.p1.implementation":
      "Next.js tabanlı performanslı vitrin, CMS destekli içerik alanları, hizmet sayfalarına internal link ağı ve ölçülebilir CTA akışları.",
    "projectDetail.p1.results":
      "Tek sayfada tarama hassasiyeti, prototip süresi ve tamamlanan proje sayısı ile güven artışı; organik aramalarda hizmet niyetli trafik.",
    "projectDetail.p1.metric1.value": "±0.02 mm",
    "projectDetail.p1.metric1.label": "Hedef tarama hassasiyeti",
    "projectDetail.p1.metric2.value": "48 saat",
    "projectDetail.p1.metric2.label": "Prototip döngüsü",
    "projectDetail.p1.metric3.value": "1480+",
    "projectDetail.p1.metric3.label": "Tamamlanan proje",
    "projectDetail.p1.metric4.value": "SCAN→CAD→PRINT",
    "projectDetail.p1.metric4.label": "Uçtan uca süreç",
    "projectDetail.p2.longDesc":
      "Aselsan ekosistemine yönelik “ailem” giriş deneyimi; minimal form alanları, güven veren tipografi ve kurumsal kimlikle uyumlu sade bir kimlik doğrulama yüzeyi.",
    "projectDetail.p2.challenge":
      "Kurumsal kullanıcılar için giriş yüzeyi güven sinyali vermek zorundaydı; fazla alan ve görsel gürültü adoption sürtünmesi yaratıyordu.",
    "projectDetail.p2.solution":
      "Minimal iki alanlı giriş, kurumsal kimlikle uyumlu tipografi ve SSO entegrasyonuna hazır sade bir kimlik doğrulama deneyimi.",
    "projectDetail.p2.implementation":
      "WCAG odaklı erişilebilirlik, mobil uyumlu responsive UI ve kurumsal SSO hook’ları için hazır giriş yüzeyi.",
    "projectDetail.p2.results":
      "Kurumsal kimlikle uyumlu, erişilebilir ve mobil öncelikli giriş deneyimi; SSO entegrasyonu için hazır yüzey.",
    "projectDetail.p2.metric1.value": "2",
    "projectDetail.p2.metric1.label": "Temel giriş alanı",
    "projectDetail.p2.metric2.value": "SSO hazır",
    "projectDetail.p2.metric2.label": "Kurumsal entegrasyon",
    "projectDetail.p2.metric3.value": "WCAG odaklı",
    "projectDetail.p2.metric3.label": "Erişilebilirlik",
    "projectDetail.p2.metric4.value": "Mobil uyumlu",
    "projectDetail.p2.metric4.label": "Responsive UI",
    "projectDetail.p3.longDesc":
      "Outfica için AI araçları (Outfit Generator, Flat Lay, Dress Up vb.) ve katalog akışını tek çatıda toplayan karanlık tema SaaS paneli; kredi, kuyruk ve kampanya alanlarıyla operasyon odaklı düzen.",
    "projectDetail.p3.metric1.value": "6+",
    "projectDetail.p3.metric1.label": "AI araç modülü",
    "projectDetail.p3.metric2.value": "Gerçek zamanlı",
    "projectDetail.p3.metric2.label": "Katalog önizleme",
    "projectDetail.p3.metric3.value": "Dark UI",
    "projectDetail.p3.metric3.label": "Uzun oturum konforu",
    "projectDetail.p3.metric4.value": "Çok dilli",
    "projectDetail.p3.metric4.label": "Genişletilebilir yapı",
    "projectDetail.p4.longDesc":
      "Ztech EdTech ana vitrin; yapay zeka vurgulu başlık, çocuk dostu görsel dil ve yaz kampı / deneme dersi çağrılarıyla dönüşüm odaklı bir eğitim markası sunumu.",
    "projectDetail.p4.challenge":
      "EdTech markası karmaşık AI mesajını ailelere güven vererek iletemiyordu; kampanya dönüşümleri düşüktü.",
    "projectDetail.p4.solution":
      "Çocuk odaklı görsel dil, net value proposition ve deneme dersi / kampanya CTA hiyerarşisi ile dönüşüm odaklı landing.",
    "projectDetail.p4.implementation":
      "Responsive vitrin, performans optimizasyonu, SEO uyumlu içerik yapısı ve lead formları.",
    "projectDetail.p4.results":
      "Marka mesajında netlik, kampanya sayfalarında daha yüksek engagement ve lead kalitesi.",
    "projectDetail.p4.metric1.value": "AI vurgulu",
    "projectDetail.p4.metric1.label": "Ana mesaj",
    "projectDetail.p4.metric2.value": "Çok segment",
    "projectDetail.p4.metric2.label": "Kurs & kamp",
    "projectDetail.p4.metric3.value": "Marka renkleri",
    "projectDetail.p4.metric3.label": "Tutarlı palet",
    "projectDetail.p4.metric4.value": "Mobil öncelik",
    "projectDetail.p4.metric4.label": "Veli kitlesi",
    "projectDetail.p5.longDesc":
      "Royal Seginus Hotel ana sayfası; rezervasyon çubuğu, tarih ve misafir seçimi ile doğrudan dönüşüm, gece fotoğrafı ve konfor mesajlarıyla Stone Group kalitesini anlatır.",
    "projectDetail.p5.metric1.value": "360°",
    "projectDetail.p5.metric1.label": "Konaklama hikâyesi",
    "projectDetail.p5.metric2.value": "Tek ekranda",
    "projectDetail.p5.metric2.label": "Rezervasyon akışı",
    "projectDetail.p5.metric3.value": "Çok dil",
    "projectDetail.p5.metric3.label": "Misafir segmenti",
    "projectDetail.p5.metric4.value": "Spa & havuz",
    "projectDetail.p5.metric4.label": "Öne çıkan olanaklar",
    "projectDetail.p6.longDesc":
      "Kolektif Fuarcılık web sitesi; İstanbul merkezli iletişim, fuarcılık portföy slider’ı ve markanızı sahneye taşıyan kurumsal metin akışı ile B2B güven oluşturur.",
    "projectDetail.p6.challenge":
      "Fuarcılık portföyü dağınık kanallarda sunuluyor; uluslararası alıcılar için tek güvenilir dijital vitrin yoktu.",
    "projectDetail.p6.solution":
      "International exhibition partner konumlandırması ile portföy slider, kurumsal anlatım ve net iletişim kanalları.",
    "projectDetail.p6.implementation":
      "Kurumsal web vitrini, proje galerisi, çok dilli SEO hazırlığı ve iletişim dönüşüm noktaları.",
    "projectDetail.p6.results":
      "B2B alıcılar için tek kaynak vitrin; portföy keşfinde daha düşük sürtünme ve daha güçlü marka algısı.",
    "projectDetail.p6.metric1.value": "800 m²",
    "projectDetail.p6.metric1.label": "Üretim alanı",
    "projectDetail.p6.metric2.value": "20+ yıl",
    "projectDetail.p6.metric2.label": "Deneyim",
    "projectDetail.p6.metric3.value": "Uçtan uca",
    "projectDetail.p6.metric3.label": "Stand üretimi",
    "projectDetail.p6.metric4.value": "Slider vitrin",
    "projectDetail.p6.metric4.label": "Portföy odaklı",
    "projectDetail.p7.longDesc":
      "LC Waikiki ana vitrininde yaz koleksiyonu kampanyası; güçlü hero görseli, kategori navigasyonu ve kampanya mesajlarıyla yüksek trafikli perakende deneyimi.",
    "projectDetail.p7.challenge":
      "Yüksek trafikli moda perakendesi, sezon kampanyasını kadın·erkek·çocuk kategori derinliğiyle aynı anda sunmak zorundaydı.",
    "projectDetail.p7.solution":
      "Güçlü hero görseli, sezon kampanya banner’ı ve arama merkezli ürün keşfiyle kategori mimarisi.",
    "projectDetail.p7.implementation":
      "Global ölçekte performans odaklı UI; kategori navigasyonu ve kampanya mesajları için vitrin düzeni.",
    "projectDetail.p7.results":
      "Kategori derinliği, sezon kampanya vitrini ve arama merkezli ürün keşfi odaklı perakende deneyimi.",
    "projectDetail.p7.metric1.value": "Kategori derinliği",
    "projectDetail.p7.metric1.label": "Kadın·Erkek·Çocuk",
    "projectDetail.p7.metric2.value": "Sezon kampanya",
    "projectDetail.p7.metric2.label": "Öne çıkan banner",
    "projectDetail.p7.metric3.value": "Global ölçek",
    "projectDetail.p7.metric3.label": "Performans odaklı UI",
    "projectDetail.p7.metric4.value": "Arama merkezli",
    "projectDetail.p7.metric4.label": "Ürün keşfi",
    "projectDetail.p8.longDesc":
      "evarkadaşı platformu; adres ve şehir bazlı arama, oda/ev seçimi ve Türkiye şehir özetleri ile kullanıcıyı hızlıca doğru ilana yönlendirir.",
    "projectDetail.p8.metric1.value": "835+",
    "projectDetail.p8.metric1.label": "Örnek ilan hacmi",
    "projectDetail.p8.metric2.value": "Şehir bazlı",
    "projectDetail.p8.metric2.label": "Keşif akışı",
    "projectDetail.p8.metric3.value": "Çift mod",
    "projectDetail.p8.metric3.label": "Oda / ev arama",
    "projectDetail.p8.metric4.value": "Sıcak UI",
    "projectDetail.p8.metric4.label": "Güven veren ton",
    "projectDetail.p9.longDesc":
      "Bool için Knorr Professional kampanya vitrininde ürün odaklı anlatım; gıda tedarik güveni, markalı içerik ve B2B sipariş kanallarına yönlendiren yapı.",
    "projectDetail.p9.metric1.value": "B2B odaklı",
    "projectDetail.p9.metric1.label": "Tedarik hattı",
    "projectDetail.p9.metric2.value": "Marka kampanya",
    "projectDetail.p9.metric2.label": "Knorr Professional",
    "projectDetail.p9.metric3.value": "Ürün çeşidi",
    "projectDetail.p9.metric3.label": "Kategori vitrinleri",
    "projectDetail.p9.metric4.value": "Kurumsal güven",
    "projectDetail.p9.metric4.label": "2010’dan beri",
    "projectDetail.p10.longDesc":
      "Backdoor Tickets arayüzü; gece ekonomisi ve promoter operasyonları için yoğun giriş senaryolarına uygun SaaS landing düzeni.",
    "projectDetail.p10.metric1.value": "Promoter",
    "projectDetail.p10.metric1.label": "Operasyon takibi",
    "projectDetail.p10.metric2.value": "Crunch-time",
    "projectDetail.p10.metric2.label": "Yoğun giriş",
    "projectDetail.p10.metric3.value": "Veri kontrolü",
    "projectDetail.p10.metric3.label": "Sahne odaklı SaaS",
    "projectDetail.p10.metric4.value": "Demo akışı",
    "projectDetail.p10.metric4.label": "Erken erişim",

    // Process
    "process.tag": "SÜRECİMİZ",
    "process.title": "Siz anlatırsınız, biz yol haritasını çizeriz.",
    "process.docs":
      "Her adımda profesyonel dokümanlar hazırlıyoruz: keşif raporu, teknik spesifikasyon, tasarım kılavuzu, test planı ve daha fazlası. Hiçbir detay havada kalmaz.",
    "process.step1.title": "Keşif & Strateji",
    "process.step1.desc":
      "Projenizin hedeflerini, kullanıcı ihtiyaçlarını ve teknik gereksinimleri derinlemesine analiz ediyoruz. Bu aşamada kapsamlı bir keşif dokümanı hazırlayarak tüm paydaşlarla ortak bir vizyon oluşturuyoruz.",
    "process.step2.title": "Tasarım & Prototip",
    "process.step2.desc":
      "Wireframe'lerden yüksek kaliteli prototiplere kadar, kullanıcı deneyimini mükemmelleştiriyoruz. Detaylı tasarım spesifikasyon dokümanları ile her karar şeffaf ve izlenebilir.",
    "process.step3.title": "Geliştirme & Test",
    "process.step3.desc":
      "En güncel teknolojilerle kod yazıyor, kapsamlı testlerle kaliteyi garanti altına alıyoruz. Teknik dokümantasyon ve test raporları ile süreç boyunca tam görünürlük sağlıyoruz.",
    "process.step4.title": "Lansman & Büyüme",
    "process.step4.desc":
      "Ürününüzü pazara sunuyor, performans takibi ve sürekli optimizasyonla büyümenizi destekliyoruz. Lansman sonrası analiz raporları ve büyüme planı ile yolculuk devam ediyor.",
    "process.step1.badge": "STEP 01",
    "process.step2.badge": "STEP 02",
    "process.step3.badge": "STEP 03",
    "process.step4.badge": "STEP 04",

    // Testimonials
    "testimonials.tag": "MÜŞTERİ YORUMLARI",
    "testimonials.title": "Ne diyorlar.",

    // Tech
    "tech.tag": "TEKNOLOJİLER",
    "tech.title": "Kullandığımız araçlar.",
    "tech.moreLine1":
      "Projenizin ihtiyacına göre istediğiniz teknolojiyi kullanıyoruz.",
    "tech.moreLine2": "Yukarıdakiler sadece başlangıç.",

    // CTA
    "cta.tag": "HADİ BAŞLAYALIM",
    "cta.title": "Fikirden ürüne. Sıfırdan zirveye.",
    "cta.desc": "Doğru teknoloji, keskin strateji ve tutkulu bir ekip.",
    "cta.button": "Projenizi Konuşalım",
    "cta.secondary": "Neler Yaptık?",

    // About
    "about.tag": "HAKKIMIZDA",
    "about.title": "Strateji, tasarım ve mühendislik tek ekipte.",
    "about.desc":
      "Figtures, dijital ürünleri keşiften canlıya kadar tek ekiple teslim eder. Ürün kararlarını verenler, arayüzü tasarlayan ve kodu yazan ekiple aynı masada çalışır; el değiştirirken kaybolan zaman ve kalite olmaz.",
    "about.seoTitle": "Figtures Hakkında | İstanbul Yazılım ve AI Product Studio",
    "about.seoDescription":
      "Figtures, Ataşehir İstanbul merkezli product studio. AI, özel yazılım, SaaS/MVP, web ve mobil projeleri keşiften canlıya tek ekiple yürütür. Süreç, ekip ve şirket bilgileri.",
    "about.loading": "Hakkımızda içeriği yükleniyor...",
    "about.error": "Hakkımızda içeriği yüklenemedi.",
    "about.valuesTitle": "Çalışma ilkelerimiz",
    "about.visionTitle": "Vizyon",
    "about.missionTitle": "Misyon",
    "about.defaultVision":
      "Teknolojiyi, iş hedefleriyle uyumlu ve sürdürülebilir ürünlere dönüştürmek.",
    "about.defaultMission":
      "Ürün stratejisi, tasarım ve mühendisliği tek akışta birleştirerek ölçülebilir değer üretmek.",
    "about.value1.title": "Önce kapsam, sonra kod",
    "about.value1.desc": "Geliştirme, yazılı keşif dokümanı ve net kabul kriterleri üzerinde uzlaşıldıktan sonra başlar.",
    "about.value2.title": "Demo değil, production",
    "about.value2.desc": "Test, güvenlik, performans ve izlenebilirlik teslimatın parçasıdır.",
    "about.value3.title": "Açık ilerleme",
    "about.value3.desc": "Düzenli demolar, paylaşılan yol haritası ve kayıt altına alınan teknik kararlar.",

    // Contact
    "contact.tag": "İLETİŞİM",
    "contact.title": "Bir sonraki dijital ürününüzü birlikte planlayalım.",
    "contact.desc":
      "İş hedeflerinizi, mevcut sistemlerinizi ve zaman planınızı paylaşın. Talebiniz, alanında deneyimli ürün ve mühendislik ekibimize iletilir. İlk görüşmede ihtiyaçlarınızı değerlendirir, size uygun teslimat modelini ve sonraki adımları birlikte belirleriz.",
    "contact.promise":
      "Paylaştığınız bilgiler gizli tutulur ve yalnızca talebinizin değerlendirilmesi için kullanılır. Talep etmeniz halinde görüşme öncesinde gizlilik sözleşmesi (NDA) imzalayabiliriz.",
    "contact.step1": "Talebinizi iletin",
    "contact.step2": "Keşif görüşmesi",
    "contact.step3": "Teklif ve yol haritası",
    "contact.projectType": "Proje türü (opsiyonel)",
    "contact.projectTypePlaceholder": "Seçin...",
    "contact.projectType.ai": "AI / Otomasyon",
    "contact.projectType.saas": "SaaS / MVP",
    "contact.projectType.mobile": "Mobil uygulama",
    "contact.projectType.custom": "Özel yazılım",
    "contact.projectType.other": "Diğer",
    "contact.hearAbout": "Bizi nereden duydunuz? (opsiyonel)",
    "contact.hearAboutPlaceholder": "Seçin...",
    "contact.hearAbout.search_google": "Google araması",
    "contact.hearAbout.ai_assistant": "ChatGPT / yapay zeka asistanı",
    "contact.hearAbout.linkedin": "LinkedIn",
    "contact.hearAbout.instagram": "Instagram",
    "contact.hearAbout.referral": "Tavsiye / tanıdık",
    "contact.hearAbout.blog": "Blog / içerik",
    "contact.hearAbout.event": "Etkinlik / konferans",
    "contact.hearAbout.other": "Diğer",
    "contact.name": "Adınız",
    "contact.email": "E-posta",
    "contact.message": "Proje detayları",
    "contact.messagePlaceholder":
      "İş hedefi, hedef kullanıcılar, mevcut sistemler, planlanan takvim ve bütçe aralığı gibi bilgileri paylaşabilirsiniz.",
    "contact.budgetToolHint":
      "Bütçe için önce ücretsiz proje maliyet tahmincisindeki örnek TRY bantlarına (KDV hariç) bakabilirsiniz; formda gördüğünüz aralığı veya keşif notlarınızı yazmanız yeterli.",
    "contact.send": "Görüşme talep edin",
    "contact.info.title": "Doğrudan ulaşın",
    "contact.info.email": "contact@figtures.com",
    "contact.info.location":
      "İçerenköy Mah. Topçu İbrahim Sok. Quick Tower Sit. No: 8-10D, Ataşehir/İstanbul",
    "contact.infoLabel.email": "E-posta",
    "contact.infoLabel.phone": "Telefon",
    "contact.infoLabel.location": "Konum",
    "contact.infoLabel.social": "Sosyal",
    "contact.heroStat3": "Ortalama Yanıt",
    "contact.heroStat3Desc":
      "Tüm talepler bir iş günü içinde ekibimiz tarafından yanıtlanır.",

    // Testimonials items (portföydeki gerçek müşteriler — kişi adı yok, marka atıfı)

    // Blog
    "blog.tag": "BLOG",
    "blog.title": "İçgörüler.",
    "blog.desc": "Teknoloji, tasarım ve inovasyon hakkında düşüncelerimiz.",
    "blog.all": "Tüm Yazılar",
    "blog.notFound": "Yazı bulunamadı",
    "blog.back": "Bloga dön",
    "blog.share.label": "Paylaş",
    "blog.share.groupLabel": "Bu yazıyı paylaş",
    "blog.share.facebook": "Facebook’ta paylaş",
    "blog.share.whatsapp": "WhatsApp’ta paylaş",
    "blog.share.reddit": "Reddit’te paylaş",
    "blog.share.x": "X’te paylaş",
    "blog.share.copy": "Bağlantıyı kopyala",
    "blog.share.copied": "Bağlantı panoya kopyalandı.",
    "blog.share.copyFailed": "Kopyalanamadı; bağlantıyı elle seçebilirsiniz.",

    // Footer
    "footer.rights": "© 2026 Figtures. Tüm hakları saklıdır.",
    "footer.tagline": "Mühendislik mükemmelliği ile dijital ürünler.",
    "footer.aiAnswers": "AI Cevaplar",
    "footer.entityProfiles": "Doğrulanmış profiller",
    "footer.linkedin": "LinkedIn",
    "footer.privacy": "Gizlilik Politikası",
    "footer.terms": "Kullanım Koşulları",
    "footer.kvkk": "KVKK Aydınlatma Metni",
    "footer.deliveryRefund": "Teslim, İptal ve İade",
    "footer.cookieSettings": "Çerez Ayarları",
    "contact.phone.aria": "Bizi arayın",
  },
  en: {
    "common.loading": "Loading...",
    "common.error": "An error occurred.",
    "common.more": "More",

    "nav.services": "Services",
    "nav.solutions": "Solutions",
    "nav.projects": "Projects",
    "nav.tools": "Tools",
    "nav.about": "About",
    "nav.contact": "Contact",
    "nav.blog": "Blog",
    "nav.cta": "Start Project",

    "hero.tag": "PRODUCT STUDIO",
    "hero.brand": "Figtures",
    "hero.title1": "Tell us what",
    "hero.title2": "you need.",
    "hero.title3": "We'll handle the rest.",
    "hero.cta1": "Describe Your Project",
    "hero.cta2": "Our Solutions",

    "home.geoDirectAnswer":
      "Global remote product studio · AI, custom software, SaaS/MVP, mobile · Turkey, Europe, MENA, UK, US & worldwide · figtures.com/contact",
    "home.brandAnswer":
      "Figtures is a product studio offering custom software, AI integration, SaaS, and mobile app development.",

    "marquee.text":
      "AI IMPLEMENTATION • SAAS DEVELOPMENT • PRODUCT DESIGN • FULL-STACK ENGINEERING • DIGITAL TRANSFORMATION • ",

    "services.tag": "OUR EXPERTISE",
    "services.seoTitle": "Custom Software & AI Development Services",
    "services.title": "You describe it, we solve it.",
    "services.desc":
      "AI products, SaaS MVPs, and custom software. One team from discovery through production.",

    "solutions.tag": "SOLUTIONS",
    "solutions.seoTitle": "Product Studio Solutions & Delivery Model",
    "solutions.title": "How we work as a product studio",
    "solutions.desc":
      "Figtures is a global product studio covering AI development, SaaS/MVP, custom software, mobile apps, and UI/UX design, working as one team from discovery through production.",
    "solutions.studioTitle": "Who is Figtures?",
    "solutions.studioBody":
      "We run product strategy, design, and engineering in one lane for startup and enterprise teams. The solution pages below focus on specific business problems. See Services for our full delivery catalog.",
    "solutions.featuredTitle": "Featured solution tracks",
    "solutions.allTitle": "All solution pages",
    "solutions.servicesCta": "Full services catalog",
    "solutions.projectsCta": "Our projects",
    "solutions.contactCta": "Send a project brief",
    "solutions.servicesNote":
      "Custom software, web development, SaaS/MVP, mobile apps, and startup product engineering are detailed on the Services page.",

    "programmatic.hub.title": "City-focused solutions",
    "programmatic.hub.subtitle":
      "Pick location and expertise; each page links contextually into our content.",
    "programmatic.sameCity": "Other services in this city",
    "programmatic.sameService": "This service in other cities",
    "programmatic.cta": "Request a quote",

    "projects.tag": "OUR PROJECTS",
    "projects.seoTitle": "Software Projects & Case Studies",
    "projects.title": "Solutions we've built.",
    "projects.desc":
      "Every project is a unique challenge. Here are some of our solutions.",
    "projects.view": "View Details",
    "projects.all": "View All Projects",
    "projects.notFound": "Project not found",
    "projects.back": "Back to Projects",
    "projects.visit": "View project live",
    "projects.visitSub":
      "Follow demo, case study or source code links to explore.",
    "projects.openLink": "Open Link",
    "seo.page.projects.title": "Software Projects & Case Studies",
    "seo.page.projects.description":
      "Our AI, SaaS, corporate web, and product UI work: the technologies used, problems solved, and outcomes delivered for clients in Turkey and abroad.",
    "projects.p1.title": "3D Yanında: Digital Production",
    "projects.p1.desc":
      "Corporate showcase for industrial reverse engineering: 3D scan, CAD, and precision manufacturing storytelling.",
    "projects.p1.tags": "Digital Manufacturing,Industrial 3D,Turkey",
    "projects.p2.title": "ailem · Aselsan",
    "projects.p2.desc":
      "Minimal enterprise login aligned with the “Bi’ Tıkla Yanında” brand promise.",
    "projects.p2.tags": "Enterprise Portal,Login UX,Identity",
    "projects.p3.title": "Outfica: AI Fashion",
    "projects.p3.desc":
      "Dark-mode SaaS dashboard for AI styling tools and catalog workflows.",
    "projects.p3.tags": "AI,Fashion,SaaS",
    "projects.p4.title": "Ztech EdTech",
    "projects.p4.desc":
      "Landing experience for AI & technology education programs aimed at families.",
    "projects.p4.tags": "EdTech,AI,Education",
    "projects.p5.title": "Royal Seginus Hotel",
    "projects.p5.desc":
      "Stone Group hotel homepage with booking bar and immersive night hero imagery.",
    "projects.p5.tags": "Hospitality,Booking,Travel",
    "projects.p6.title": "Kolektif Exhibition Partner",
    "projects.p6.desc":
      "Corporate exhibition portfolio with bold sidebar identity and project slider.",
    "projects.p6.tags": "Exhibitions,Portfolio,B2B",
    "projects.p7.title": "LC Waikiki: Campaign",
    "projects.p7.desc":
      "High-traffic retail homepage with seasonal hero and category navigation.",
    "projects.p7.tags": "E-commerce,Retail,Fashion",
    "projects.p8.title": "evarkadaşı",
    "projects.p8.desc":
      "Roommate discovery with city-first search and dual room/home modes.",
    "projects.p8.tags": "Marketplace,Rental,Cities",
    "projects.p9.title": "Bool · Knorr Professional",
    "projects.p9.desc":
      "Food supply partner site featuring Knorr Professional campaign storytelling.",
    "projects.p9.tags": "Food Supply,B2B,Brand",
    "projects.p10.title": "Backdoor Tickets",
    "projects.p10.desc":
      "Nightlife ticketing SaaS landing focused on promoter ops and door control.",
    "projects.p10.tags": "Ticketing,Events,SaaS",
    "projectDetail.back": "Back to Projects",
    "projectDetail.challengeTitle": "Challenge we identified",
    "projectDetail.solutionTitle": "Our approach",
    "projectDetail.implementationTitle": "What we implemented",
    "projectDetail.resultsTag": "RESULTS",
    "projectDetail.resultsTitle": "Measurable outcomes.",
    "projectDetail.galleryTag": "GALLERY",
    "projectDetail.galleryTitle": "Screenshots.",
    "projectDetail.ctaTitle": "Have a similar project?",
    "projectDetail.ctaDesc":
      "Let's bring your project to life. Get in touch today.",
    "projectDetail.ctaButton": "Start a Project",
    "projectDetail.p1.longDesc":
      "Digital manufacturing workshop positioning, with hero metrics for scan precision, rapid prototyping cadence, and completed jobs; tailored for automotive spares and industrial replacement narratives.",
    "projectDetail.p1.challenge":
      "The workshop website failed to communicate technical precision and speed; B2B buyers lacked trust signals.",
    "projectDetail.p1.solution":
      "Metric-led corporate site centered on SCAN→CAD→PRINT with clear lead capture.",
    "projectDetail.p1.implementation":
      "Performance-focused Next.js showcase, CMS-ready content zones, internal links to services, measurable CTAs.",
    "projectDetail.p1.results":
      "Stronger trust via precision, prototype cadence, and shipped-project metrics; improved service-intent organic traffic.",
    "projectDetail.p1.metric1.value": "±0.02 mm",
    "projectDetail.p1.metric1.label": "Scan precision target",
    "projectDetail.p1.metric2.value": "48 h",
    "projectDetail.p1.metric2.label": "Prototype cadence",
    "projectDetail.p1.metric3.value": "1480+",
    "projectDetail.p1.metric3.label": "Shipped projects",
    "projectDetail.p1.metric4.value": "SCAN→CAD→PRINT",
    "projectDetail.p1.metric4.label": "End-to-end flow",
    "projectDetail.p2.longDesc":
      "Enterprise authentication surface for the “ailem” initiative, with restrained inputs, trustworthy typography, and identity-aligned visuals built for Aselsan audiences.",
    "projectDetail.p2.challenge":
      "The login surface had to signal trust for enterprise users; extra fields and visual noise added adoption friction.",
    "projectDetail.p2.solution":
      "A two-field login flow with corporate-aligned typography and a clean authentication experience ready for SSO integration.",
    "projectDetail.p2.implementation":
      "WCAG-first accessibility, responsive mobile UI, and an enterprise login surface prepared for SSO hooks.",
    "projectDetail.p2.results":
      "Corporate-aligned, accessible, mobile-first login experience with an SSO-ready integration surface.",
    "projectDetail.p2.metric1.value": "2",
    "projectDetail.p2.metric1.label": "Core login fields",
    "projectDetail.p2.metric2.value": "SSO-ready",
    "projectDetail.p2.metric2.label": "Enterprise hooks",
    "projectDetail.p2.metric3.value": "WCAG-first",
    "projectDetail.p2.metric3.label": "Accessibility posture",
    "projectDetail.p2.metric4.value": "Responsive",
    "projectDetail.p2.metric4.label": "Mobile parity",
    "projectDetail.p3.longDesc":
      "Outfica workspace surfacing AI styling utilities (Outfit Generator, Flat Lay, Dress Up, etc.) alongside catalog tooling, in a dark UI tuned for long creative sessions.",
    "projectDetail.p3.metric1.value": "6+",
    "projectDetail.p3.metric1.label": "AI surface modules",
    "projectDetail.p3.metric2.value": "Live previews",
    "projectDetail.p3.metric2.label": "Catalog iteration",
    "projectDetail.p3.metric3.value": "Dark UI",
    "projectDetail.p3.metric3.label": "Reduced eye strain",
    "projectDetail.p3.metric4.value": "i18n-ready",
    "projectDetail.p3.metric4.label": "Expandable shell",
    "projectDetail.p4.longDesc":
      "Ztech landing emphasising AI literacy for kids, with a vibrant hero, discovery-lesson CTAs, and modular storytelling blocks for families.",
    "projectDetail.p4.challenge":
      "The EdTech brand struggled to explain AI value to parents; campaign conversion was weak.",
    "projectDetail.p4.solution":
      "Family-friendly visual language, sharp value proposition, and trial/campaign CTA hierarchy.",
    "projectDetail.p4.implementation":
      "Responsive landing, performance tuning, SEO-ready content structure, and lead forms.",
    "projectDetail.p4.results":
      "Clearer brand story, stronger campaign engagement, and higher-quality inbound leads.",
    "projectDetail.p4.metric1.value": "AI-led",
    "projectDetail.p4.metric1.label": "Hero narrative",
    "projectDetail.p4.metric2.value": "Multi-offer",
    "projectDetail.p4.metric2.label": "Courses & camps",
    "projectDetail.p4.metric3.value": "Brand palette",
    "projectDetail.p4.metric3.label": "Consistent system",
    "projectDetail.p4.metric4.value": "Mobile-first",
    "projectDetail.p4.metric4.label": "Parent audience",
    "projectDetail.p5.longDesc":
      "Royal Seginus Hotel homepage blending panoramic night photography with frictionless booking controls under the Stone Group umbrella.",
    "projectDetail.p5.metric1.value": "360°",
    "projectDetail.p5.metric1.label": "Stay storytelling",
    "projectDetail.p5.metric2.value": "Single strip",
    "projectDetail.p5.metric2.label": "Booking UX",
    "projectDetail.p5.metric3.value": "Localized",
    "projectDetail.p5.metric3.label": "Guest journeys",
    "projectDetail.p5.metric4.value": "Spa & pool",
    "projectDetail.p5.metric4.label": "Signature amenities",
    "projectDetail.p6.longDesc":
      "Kolektif Fuarcılık: Istanbul HQ credentials, exhibition slider, and editorial copy engineered for trade-show buyers.",
    "projectDetail.p6.challenge":
      "Exhibition work was scattered across channels without one credible digital showcase for international buyers.",
    "projectDetail.p6.solution":
      "International exhibition partner positioning with portfolio slider, corporate narrative, and clear contact paths.",
    "projectDetail.p6.implementation":
      "Corporate web showcase, project gallery, SEO-ready structure, and conversion-focused contact points.",
    "projectDetail.p6.results":
      "Single source of truth for B2B buyers; lower friction in portfolio discovery and stronger brand trust.",
    "projectDetail.p6.metric1.value": "800 m²",
    "projectDetail.p6.metric1.label": "Fabrication space",
    "projectDetail.p6.metric2.value": "20+ yrs",
    "projectDetail.p6.metric2.label": "Field expertise",
    "projectDetail.p6.metric3.value": "Turnkey",
    "projectDetail.p6.metric3.label": "Stand builds",
    "projectDetail.p6.metric4.value": "Carousel hero",
    "projectDetail.p6.metric4.label": "Portfolio-first",
    "projectDetail.p7.longDesc":
      "LC Waikiki seasonal hero spotlighting summer dresses, with category breadth for women, men, kids, and campaign storytelling tuned for massive retail traffic.",
    "projectDetail.p7.challenge":
      "High-traffic fashion retail needed to present a seasonal campaign alongside deep women·men·kids category navigation.",
    "projectDetail.p7.solution":
      "A hero-led campaign layout with seasonal banners and search-led product discovery across category architecture.",
    "projectDetail.p7.implementation":
      "Performance-aware UI at global scale with category navigation and campaign messaging on the storefront.",
    "projectDetail.p7.results":
      "Retail experience centered on category depth, seasonal campaign spotlight, and search-led product discovery.",
    "projectDetail.p7.metric1.value": "Deep nav",
    "projectDetail.p7.metric1.label": "Women·Men·Kids",
    "projectDetail.p7.metric2.value": "Seasonal",
    "projectDetail.p7.metric2.label": "Campaign spotlight",
    "projectDetail.p7.metric3.value": "Global scale",
    "projectDetail.p7.metric3.label": "Perf-aware UI",
    "projectDetail.p7.metric4.value": "Search-led",
    "projectDetail.p7.metric4.label": "Product discovery",
    "projectDetail.p8.longDesc":
      "evarkadaşı onboarding: hybrid search for addresses/cities, roommate vs. whole-home modes, and friendly lifestyle photography.",
    "projectDetail.p8.metric1.value": "835+",
    "projectDetail.p8.metric1.label": "Listing momentum",
    "projectDetail.p8.metric2.value": "City-first",
    "projectDetail.p8.metric2.label": "Discovery UX",
    "projectDetail.p8.metric3.value": "Dual intent",
    "projectDetail.p8.metric3.label": "Room vs home",
    "projectDetail.p8.metric4.value": "Warm tone",
    "projectDetail.p8.metric4.label": "Trust-building UI",
    "projectDetail.p9.longDesc":
      "Bool × Knorr Professional narrative: appetizing hero, bouillon lineup, and supply-chain credibility moments for foodservice buyers.",
    "projectDetail.p9.metric1.value": "B2B-first",
    "projectDetail.p9.metric1.label": "Supply desk",
    "projectDetail.p9.metric2.value": "Brand promo",
    "projectDetail.p9.metric2.label": "Knorr lineup",
    "projectDetail.p9.metric3.value": "SKU depth",
    "projectDetail.p9.metric3.label": "Category shelves",
    "projectDetail.p9.metric4.value": "Since 2010",
    "projectDetail.p9.metric4.label": "Heritage proof",
    "projectDetail.p10.longDesc":
      "Backdoor Tickets: German-market SaaS landing emphasising promoter tracking, crunch-time door ops, and data sovereignty for venues.",
    "projectDetail.p10.metric1.value": "Promoters",
    "projectDetail.p10.metric1.label": "Ops telemetry",
    "projectDetail.p10.metric2.value": "Crunch-time",
    "projectDetail.p10.metric2.label": "Door throughput",
    "projectDetail.p10.metric3.value": "Data control",
    "projectDetail.p10.metric3.label": "Venue-grade SaaS",
    "projectDetail.p10.metric4.value": "Demo flow",
    "projectDetail.p10.metric4.label": "Early access",

    "process.tag": "OUR PROCESS",
    "process.title": "You describe it, we map the roadmap.",
    "process.docs":
      "At every step, we prepare professional documentation: discovery report, technical specification, design guideline, test plan, and more. No detail is left ambiguous.",
    "process.step1.title": "Discovery & Strategy",
    "process.step1.desc":
      "We deeply analyze your project goals, user needs, and technical requirements. In this phase, we prepare a comprehensive discovery document and align all stakeholders around a shared vision.",
    "process.step2.title": "Design & Prototype",
    "process.step2.desc":
      "From wireframes to high-fidelity prototypes, we refine the user experience end to end. Detailed design specification documents keep every decision transparent and traceable.",
    "process.step3.title": "Development & Testing",
    "process.step3.desc":
      "We build with modern technologies and ensure quality through comprehensive testing. Technical documentation and test reports provide full visibility throughout the process.",
    "process.step4.title": "Launch & Growth",
    "process.step4.desc":
      "We launch your product, then support growth with performance monitoring and continuous optimization. The journey continues with post-launch analysis reports and a growth roadmap.",
    "process.step1.badge": "STEP 01",
    "process.step2.badge": "STEP 02",
    "process.step3.badge": "STEP 03",
    "process.step4.badge": "STEP 04",

    "testimonials.tag": "TESTIMONIALS",
    "testimonials.title": "What they say.",

    "tech.tag": "TECHNOLOGIES",
    "tech.title": "Our tech stack.",
    "tech.moreLine1": "We use the technologies your project needs.",
    "tech.moreLine2": "What you see above is just the beginning.",

    "cta.tag": "LET'S BEGIN",
    "cta.title": "From idea to product. From zero to the top.",
    "cta.desc": "The right technology, sharp strategy, and a passionate team.",
    "cta.button": "Let's Talk",
    "cta.secondary": "See Our Work",

    "about.tag": "ABOUT US",
    "about.title": "Strategy, design and engineering in one team.",
    "about.desc":
      "Figtures delivers digital products from discovery to production with a single team. The people making product decisions work alongside the people designing the interface and writing the code, so nothing is lost in handoffs.",
    "about.seoTitle": "About Figtures | Istanbul Software and AI Product Studio",
    "about.seoDescription":
      "Figtures is a product studio in Ataşehir, Istanbul, delivering AI, custom software, SaaS/MVP, web and mobile products end to end. Process, team and company facts.",
    "about.loading": "Loading about content...",
    "about.error": "Failed to load about content.",
    "about.valuesTitle": "Operating principles",
    "about.visionTitle": "Vision",
    "about.missionTitle": "Mission",
    "about.defaultVision":
      "Turn technology into sustainable products aligned with business outcomes.",
    "about.defaultMission":
      "Create measurable impact by combining product strategy, design, and engineering in one delivery flow.",
    "about.value1.title": "Scope before code",
    "about.value1.desc": "Development starts only after a written discovery document and clear acceptance criteria are agreed.",
    "about.value2.title": "Production, not demos",
    "about.value2.desc": "Testing, security, performance and observability are part of the deliverable.",
    "about.value3.title": "Visible progress",
    "about.value3.desc": "Regular demos, a shared roadmap and documented technical decisions.",

    "contact.tag": "CONTACT",
    "contact.title": "Let’s plan your next digital product together.",
    "contact.desc":
      "Share your business goals, existing systems, and timeline. Your request is routed to our experienced product and engineering team. In our first conversation, we assess your needs and agree on the right delivery model and next steps.",
    "contact.promise":
      "Everything you share is kept confidential and used solely to evaluate your request. On request, we are happy to sign a non-disclosure agreement (NDA) before our first call.",
    "contact.step1": "Submit your request",
    "contact.step2": "Discovery call",
    "contact.step3": "Proposal and roadmap",
    "contact.projectType": "Project type (optional)",
    "contact.projectTypePlaceholder": "Select...",
    "contact.projectType.ai": "AI / Automation",
    "contact.projectType.saas": "SaaS / MVP",
    "contact.projectType.mobile": "Mobile app",
    "contact.projectType.custom": "Custom software",
    "contact.projectType.other": "Other",
    "contact.hearAbout": "How did you hear about us? (optional)",
    "contact.hearAboutPlaceholder": "Select...",
    "contact.hearAbout.search_google": "Google search",
    "contact.hearAbout.ai_assistant": "ChatGPT / AI assistant",
    "contact.hearAbout.linkedin": "LinkedIn",
    "contact.hearAbout.instagram": "Instagram",
    "contact.hearAbout.referral": "Referral",
    "contact.hearAbout.blog": "Blog / content",
    "contact.hearAbout.event": "Event / conference",
    "contact.hearAbout.other": "Other",
    "contact.name": "Your Name",
    "contact.email": "Email",
    "contact.message": "Project details",
    "contact.messagePlaceholder":
      "Business goal, target users, existing systems, planned timeline, and budget range.",
    "contact.budgetToolHint":
      "For budget context, start with the illustrative TRY bands (excl. VAT) in our free project cost estimator—then paste the range you saw or your discovery notes here.",
    "contact.send": "Request a consultation",
    "contact.info.title": "Reach out directly",
    "contact.info.email": "contact@figtures.com",
    "contact.info.location":
      "İçerenköy Mah. Topçu İbrahim Sok. Quick Tower Sit. No: 8-10D, Ataşehir, Istanbul, Turkey",
    "contact.infoLabel.email": "Email",
    "contact.infoLabel.phone": "Phone",
    "contact.infoLabel.location": "Location",
    "contact.infoLabel.social": "Social",
    "contact.heroStat3": "Average Response Time",
    "contact.heroStat3Desc":
      "Every request receives a response from our team within one business day.",


    "blog.tag": "BLOG",
    "blog.title": "Insights.",
    "blog.desc": "Our thoughts on technology, design, and innovation.",
    "blog.all": "All Posts",
    "blog.notFound": "Post not found",
    "blog.back": "Back to Blog",
    "blog.share.label": "Share",
    "blog.share.groupLabel": "Share this article",
    "blog.share.facebook": "Share on Facebook",
    "blog.share.whatsapp": "Share on WhatsApp",
    "blog.share.reddit": "Share on Reddit",
    "blog.share.x": "Share on X",
    "blog.share.copy": "Copy link",
    "blog.share.copied": "Link copied to clipboard.",
    "blog.share.copyFailed": "Could not copy. Try selecting the link manually.",

    "footer.rights": "© 2026 Figtures. All rights reserved.",
    "footer.tagline": "Digital products with engineering excellence.",
    "footer.aiAnswers": "AI Answers",
    "footer.entityProfiles": "Verified profiles",
    "footer.linkedin": "LinkedIn",
    "footer.privacy": "Privacy Policy",
    "footer.terms": "Terms of Service",
    "footer.kvkk": "KVKK Clarification Text",
    "footer.deliveryRefund": "Delivery, Cancellation & Refunds",
    "footer.cookieSettings": "Cookie Settings",
    "contact.phone.aria": "Call us",
  },
};

/** Written only on an explicit switch; index.html reads it to decide the "/" → "/tr" redirect. */
export const LANG_PREFERENCE_KEY = "figtures_lang_pref";
const STORAGE_KEY = LANG_PREFERENCE_KEY;
const TRANSITION_MS = 200;

function readInitialLang(pathname?: string): Lang {
  if (typeof window !== "undefined" && SEO_LOCALE_URL_STRATEGY === "path") {
    return detectLocaleFromPathname(pathname ?? window.location.pathname);
  }

  if (typeof window !== "undefined") {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "tr" || stored === "en") {
      return stored;
    }
  }

  return "tr";
}

function pulseLanguageTransition() {
  if (typeof document === "undefined") return;

  const root = document.documentElement;
  root.classList.add("language-transition");

  window.setTimeout(() => {
    root.classList.remove("language-transition");
  }, TRANSITION_MS);
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined,
);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  const [lang, setLangState] = useState<Lang>(() =>
    readInitialLang(location.pathname),
  );

  useEffect(() => {
    if (SEO_LOCALE_URL_STRATEGY !== "path") return;
    const detected = detectLocaleFromPathname(location.pathname);
    setLangState((current) => (current === detected ? current : detected));
  }, [location.pathname]);

  const setLang = useCallback((next: Lang) => {
    setLangState((current) => {
      if (current === next) return current;
      pulseLanguageTransition();
      window.localStorage.setItem(STORAGE_KEY, next);
      return next;
    });
  }, []);

  const t = useCallback(
    (key: string) => translations[lang][key] || key,
    [lang],
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context)
    throw new Error("useLanguage must be used within LanguageProvider");
  return context;
};
